export default {
	data() {
		return {
			adObserver: null,
			domObserver: null,
			observedAds: new Set(),
			maxRetries: 3,
			preloadDistance: 300,
			observeDebounceTimer: null,
		}
	},

	async mounted() {
		// 只加载 AdSense 脚本 URL，不执行广告 push
		try {
			await this.loadAdSenseScript()
		} catch (error) {
			console.error(`[AdMixin] load script failed in ${this.$options.name}:`, error)
		}
	},

	beforeDestroy() {
		this.cleanupAdSystem()
	},

	methods: {
		getAdsenseConfig() {
			return this.adsenseConfig || this.adsensConfig || this.$store?.getters?.domainConfig?.adsense || null
		},

		async initAdSystem() {
			if (typeof window === 'undefined') return
			await this.loadAdSenseScript()
			this.$nextTick(() => {
				this.setupAdObserver()
				this.observeAds()
				this.setupDomObserver()
			})
		},

		async loadAdSenseScript() {
			if (typeof window === 'undefined') return
			if (window.adsbygoogle?.loaded) return

			const config = this.getAdsenseConfig()
			if (!config?.scriptUrl) return

			const scriptKey = '__adsenseScriptPromise__'
			if (!window[scriptKey]) {
				window[scriptKey] = this.loadScript(config.scriptUrl)
			}

			try {
				await window[scriptKey]
			} catch (error) {
				window[scriptKey] = null
				console.error('[AdMixin] load script failed:', error)
			}
		},

		loadScript(url) {
			return new Promise((resolve, reject) => {
				const existed = document.querySelector(`script[src="${url}"]`)
				if (existed) {
					if (existed.dataset.loaded === 'true') {
						resolve()
						return
					}
					existed.addEventListener('load', () => resolve(), { once: true })
					existed.addEventListener('error', (error) => reject(error), { once: true })
					return
				}

				const script = document.createElement('script')
				script.src = url
				script.async = true
				script.crossOrigin = 'anonymous'
				script.onload = () => {
					script.dataset.loaded = 'true'
					resolve()
				}
				script.onerror = (error) => reject(error)
				document.head.appendChild(script)
			})
		},

		setupAdObserver() {
			if (typeof window === 'undefined') return

			if (this.adObserver) {
				this.adObserver.disconnect()
				this.adObserver = null
			}
			this.observedAds = new Set()

			if (typeof IntersectionObserver === 'undefined') return

			this.adObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => this.handleAdIntersection(entry))
				},
				{
					rootMargin: `${this.preloadDistance}px 0px ${this.preloadDistance}px 0px`,
					threshold: 0,
				},
			)
		},

		setupDomObserver() {
			if (typeof MutationObserver === 'undefined' || !this.$el) return
			if (this.domObserver) return

			this.domObserver = new MutationObserver(() => {
				if (this.observeDebounceTimer) {
					clearTimeout(this.observeDebounceTimer)
				}
				this.observeDebounceTimer = setTimeout(() => this.observeAds(), 250)
			})

			this.domObserver.observe(this.$el, {
				childList: true,
				subtree: true,
			})
		},

		observeAds() {
			if (!this.$el) return

			const adElements = this.$el.querySelectorAll('ins.adsbygoogle[data-ad-slot]')
			if (!adElements.length) return

			adElements.forEach((adElement) => {
				const loaded = adElement.getAttribute('data-ad-status')
				if (loaded || this.observedAds.has(adElement)) return

				this.observedAds.add(adElement)

				if (this.adObserver) {
					this.adObserver.observe(adElement)
				} else {
					this.loadAd(adElement)
				}
			})
		},

		handleAdIntersection(entry) {
			if (!entry?.isIntersecting) return

			const adElement = entry.target
			if (!adElement || adElement.getAttribute('data-ad-status')) return

			if (this.adObserver) {
				this.adObserver.unobserve(adElement)
			}
			this.observedAds.delete(adElement)
			this.loadAd(adElement)
		},

		// REDLINE_EXCEPTION: 广告加载逻辑因包含防抖、重试和 DOM 检查，代码块偏长
		async loadAd(adElement, retryCount = 0) {
			if (!adElement || adElement.getAttribute('data-ad-status')) return

			if (!window.adsbygoogle) {
				if (retryCount < this.maxRetries) {
					setTimeout(() => this.loadAd(adElement, retryCount + 1), 1000)
				}
				return
			}

			try {
				await this.$nextTick()
				if (!document.body.contains(adElement)) return

				if (adElement.offsetWidth <= 0) {
					if (retryCount < this.maxRetries) {
						setTimeout(() => this.loadAd(adElement, retryCount + 1), 500)
					}
					return
				}

				;(window.adsbygoogle = window.adsbygoogle || []).push({})
				adElement.setAttribute('data-ad-status', 'loaded')
			} catch (error) {
				if (retryCount < this.maxRetries) {
					setTimeout(() => this.loadAd(adElement, retryCount + 1), 1000)
				} else {
					adElement.setAttribute('data-ad-status', 'error')
				}
				console.error('[AdMixin] push failed:', error)
			}
		},

		cleanupAdSystem() {
			if (this.adObserver) {
				this.adObserver.disconnect()
				this.adObserver = null
			}

			if (this.domObserver) {
				this.domObserver.disconnect()
				this.domObserver = null
			}

			if (this.observeDebounceTimer) {
				clearTimeout(this.observeDebounceTimer)
				this.observeDebounceTimer = null
			}

			this.observedAds.clear()
		},
	},
}
